import { Component, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    atlas,
    GROUPED_INVOICE_DETAIL,
    SUPPLY_CHAIN_DIRECT_SELLER_PARENT_GROUPED_INVOICE_DETAIL
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    GetBuyerInvoiceExportService,
    InvoiceFundingStatus
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    GetPendingGroupService,
    PendingGroupRequest,
    SupplierPendingGroupItem
} from '@lsq-frontend/remote-services/financing-graphql';
import {
    Color,
    DateFormat,
    getDynamicRouterLink,
    LifecycleAware,
    StatusColor,
    Table,
    TableAppearance
} from '@lsq-frontend/common-ui';
import {
    FeatureFlagService,
    getRouterLinkById,
    Icons,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { InvoiceFundingStatusDisplay } from '../../../../supply-chain-direct.models';

@Component({
    selector: 'supply-chain-direct-seller-pending-credit-memos',
    templateUrl: './pending-credit-memos.component.html',
    styleUrls: ['./pending-credit-memos.component.scss'],
    encapsulation: ViewEncapsulation.None
})
@LifecycleAware()
export class SellerPendingCreditMemosComponent {
    public dataSource: LsqListSource<SupplierPendingGroupItem>;
    public readonly tableAppearance = TableAppearance;
    public readonly color = Color;
    public readonly statusColor = StatusColor;
    public readonly icons = Icons;
    public readonly featureFlags = FeatureFlags;
    public readonly lsqShortDateFormat = DateFormat.LsqShort;
    public readonly ineligibleStatus = InvoiceFundingStatus.INELIGIBLE;
    public readonly invoiceNumberI18n = this._i18n({
        value: 'Invoice Number',
        meaning: `Invoice number`,
        description: 'Table column header'
    });
    public readonly pendingGroupI18n = this._i18n({
        value: 'Pending Group',
        meaning: `Pending Group`,
        description: 'Table column header'
    });
    public readonly invoiceDateI18n = this._i18n({
        value: 'Invoice Date',
        meaning: `Invoice date`,
        description: 'Table column header'
    });
    public readonly dueDateI18n = this._i18n({
        value: 'Due Date',
        meaning: `Date the invoice is due to be paid by the buyer`,
        description: 'Table column header'
    });
    public readonly dueInI18n = this._i18n({
        value: 'Due In',
        meaning: `Days until the invoice is due to be paid by the buyer`,
        description: 'Table column header'
    });
    public readonly invoiceAmountI18n = this._i18n({
        value: 'Amount',
        meaning: `The money value of the invoice`,
        description: 'Table column header'
    });
    public readonly invoiceStatusI18n = this._i18n({
        value: 'Status',
        meaning: `The eligibility status of the invoice`,
        description: 'Table column header'
    });

    public readonly getCreditMemosRowClassNameBound = this._getCreditMemosRowClassName.bind(this);
    public readonly goToPendingCredMemoDetailBound = this.goToPendingCredMemoDetail.bind(this);
    public readonly isInvoiceClickableBound = this.isInvoiceClickable.bind(this);

    public readonly nullValue = Table.NullAmount;

    public readonly invoiceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 29;
            }

            if (dimensions.width > 1375) {
                return 18;
            }

            if (dimensions.width > 1199) {
                return 14;
            }
            return 29;
        })
    );

    private $isLoading = new BehaviorSubject<boolean>(true);

    constructor(
        public invoiceFundingStatusDisplay: InvoiceFundingStatusDisplay,
        public responsiveService: ResponsiveService,
        public windowReferenceService: WindowReferenceService,
        public getBuyerInvoiceExportService: GetBuyerInvoiceExportService,
        private _i18n: I18n,
        private _getPendingGroupService: GetPendingGroupService,
        private _featureFlagService: FeatureFlagService,
        private _router: Router
    ) {}

    public async ngOnInit(): Promise<void> {
        this.dataSource = new LsqListSource({
            rows$: from(
                this._getPendingGroupService
                    .executeOnce(new PendingGroupRequest())
                    .finally(() => this.$isLoading.next(false))
            ).pipe(
                map((data) => data.pendingGroup),
                map((pendingGroup) => {
                    return pendingGroup
                        ? [
                              {
                                  invoiceAmount: pendingGroup.total,
                                  invoiceNumber: pendingGroup.groupId,
                                  status: 'INELIGIBLE',
                                  dueIn: 0,
                                  isGroup: true,
                                  itemKey: pendingGroup.groupKey
                              },
                              ...(pendingGroup.items || [])
                          ]
                        : [];
                })
            ),
            loading$: this.$isLoading
        });
    }

    public trackByPendingKeys(_index: number, { itemKey }): Guid {
        return itemKey;
    }

    private _getCreditMemosRowClassName(row: SupplierPendingGroupItem): string {
        const classList = [];
        if (row.isGroup) {
            classList.push('is-group');
            classList.push('expanded');
        } else {
            classList.push('is-member-of-group');
        }

        return classList.join(' ');
    }

    public async goToPendingCredMemoDetail(row): Promise<void> {
        const groupedInvType = row.status === 'CREDIT_MEMO' ? 'creditMemo' : 'approval';
        if (row.isGroup) {
            await this._router.navigateByUrl(
                this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(
                            atlas,
                            SUPPLY_CHAIN_DIRECT_SELLER_PARENT_GROUPED_INVOICE_DETAIL,
                            {
                                noStandaloneLeadingSlash: true
                            }
                        ),
                        { invoiceKey: row.itemKey }
                    ),
                    {
                        queryParams: {
                            [FastTrackRouteQueryParams.PreviousRouteId]: PENDING_SELLER_CREDIT_MEMOS_TAB
                        }
                    }
                )
            );
        } else {
            await this._router.navigateByUrl(
                this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, GROUPED_INVOICE_DETAIL, {
                            noStandaloneLeadingSlash: true
                        }),
                        { groupedInvoiceKey: row.itemKey }
                    ),
                    {
                        queryParams: {
                            [FastTrackRouteQueryParams.PreviousRouteId]: PENDING_SELLER_CREDIT_MEMOS_TAB,
                            [FastTrackRouteQueryParams.GroupedInvoiceType]: groupedInvType
                        }
                    }
                )
            );
        }
    }

    public isInvoiceClickable(row: SupplierPendingGroupItem): boolean {
        return (
            (row.isGroup &&
                this._featureFlagService.isOnSync(FeatureFlags.PendingCreditMemosDetails)) ||
            !row.isGroup
        );
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const PENDING_SELLER_CREDIT_MEMOS_TAB = 'PENDING_SELLER_CREDIT_MEMOS_TAB';
